import 'amazon-connect-streams';
import { useEffect, useRef } from 'react';
import { useAppContext } from '../../../AppContext';
import { useChatContext } from '../../../contexts/ChatContext';
import { useEmailContext } from '../../../contexts/EmailContext';
import { useTelephonyContext } from '../../../contexts/TelephonyContext';
import { Page } from '../../../models/Page';
import { EMAIL_INITIATION_METHODS } from '../../../constants/email';

interface UseEmailLifecycleProps {
  emailContactsRef: React.MutableRefObject<string[]>;
}

export function useEmailLifecycle({ emailContactsRef }: UseEmailLifecycleProps): void {
  const { activePage } = useAppContext();
  const { chatContacts } = useChatContext();
  const { voiceContact } = useTelephonyContext();
  const {
    emailContacts,
    isReplyOpen,
    setSelectedEmail,
    addEmailContact,
    removeEmailContact
  } = useEmailContext();

  const activePageRef = useRef(activePage);
  activePageRef.current = activePage;

  const chatContactsRef = useRef(chatContacts);
  chatContactsRef.current = chatContacts;

  const voiceContactRef = useRef(voiceContact);
  voiceContactRef.current = voiceContact;

  const isReplyOpenRef = useRef(isReplyOpen);
  isReplyOpenRef.current = isReplyOpen;

  emailContactsRef.current = emailContacts;

  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    connect.contact(contact => {
      if ((contact.getType() as string) !== connect.ContactType.EMAIL) {
        return;
      }

      const initiationMethod = contact.getInitiationMethod() as string;

      if (initiationMethod?.toUpperCase() === EMAIL_INITIATION_METHODS.AGENT_REPLY) {
        return;
      }

      const contactId = contact.getContactId();

      const isFirstEmail = emailContactsRef.current.length === 0;
      const isHandlingOtherContacts = chatContactsRef.current.length > 0 || voiceContactRef.current !== null;
      const isFirstContactOverall = isFirstEmail && !isHandlingOtherContacts;

      addEmailContact(contactId);

      if (!isReplyOpenRef.current && isFirstContactOverall && activePageRef.current === Page.EMAIL) {
        setSelectedEmail(contactId);
      }

      contact.onDestroy(() => {
        const remaining = emailContactsRef.current.filter(id => id !== contactId);

        removeEmailContact(contactId);

        if (remaining.length > 0) {
          setSelectedEmail(remaining[remaining.length - 1]);
        } else {
          setSelectedEmail(null);
        }
      });
    });
  }, [
    emailContactsRef,
    addEmailContact,
    removeEmailContact,
    setSelectedEmail
  ]);
}

